'use client'

/**
 * MoneyTreemap — a squarified market-cap treemap (Finviz/TradingView style). One flat packed map,
 * tile AREA = market cap (or $ moved), COLOR = today's move. Sector FILTER is controlled by the
 * parent (chips here + the sector bars above share one selection). Dense/Clean + Cap/$-moved toggles,
 * hero logos on big tiles, and a rich hover card. Pure client layout; measures its own width.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { SECTOR_ACCENT, SECTORS, type Sector } from '@/lib/dashboard/sectorMap'
import type { NameFlow } from '@/lib/dashboard/moneyMap'

// ── squarified treemap ──────────────────────────────────────────────────────
type SqRect = { x: number; y: number; w: number; h: number; data: NameFlow }
function rowAspect(areas: number[], short: number): number {
  const sum = areas.reduce((s, a) => s + a, 0)
  if (sum <= 0) return Infinity
  const max = Math.max(...areas), min = Math.min(...areas)
  const s2 = sum * sum, sh2 = short * short
  return Math.max((sh2 * max) / s2, s2 / (sh2 * min))
}
function squarify(nodes: { value: number; data: NameFlow }[], X: number, Y: number, W: number, H: number): SqRect[] {
  const out: SqRect[] = []
  const valid = nodes.filter((n) => n.value > 0)
  if (!valid.length || W <= 0 || H <= 0) return out
  const total = valid.reduce((s, n) => s + n.value, 0)
  const scale = (W * H) / total
  const items = valid.map((n) => ({ area: n.value * scale, data: n.data }))
  let x = X, y = Y, w = W, h = H, start = 0
  while (start < items.length) {
    const short = Math.min(w, h)
    let end = start + 1
    let best = rowAspect(items.slice(start, end).map((i) => i.area), short)
    while (end < items.length) {
      const a = rowAspect(items.slice(start, end + 1).map((i) => i.area), short)
      if (a <= best) { best = a; end++ } else break
    }
    const row = items.slice(start, end)
    const thick = row.reduce((s, r) => s + r.area, 0) / short
    if (w >= h) {
      let cy = y
      for (const r of row) { const rh = r.area / thick; out.push({ x, y: cy, w: thick, h: rh, data: r.data }); cy += rh }
      x += thick; w -= thick
    } else {
      let cx = x
      for (const r of row) { const rw = r.area / thick; out.push({ x: cx, y, w: rw, h: thick, data: r.data }); cx += rw }
      y += thick; h -= thick
    }
    start = end
  }
  return out
}

// Perceptual heat ramp — richer + more separable than a flat lerp (owner 2026-07-15: "better than
// finviz"). Multi-stop emerald/rose bands with a neutral slate at flat. Stops are FRONT-LOADED so small
// moves already show a clear tint (finviz's weakness is everything near 0 looks identically gray), and
// the endpoints saturate to a deep emerald/rose that matches the site palette without going neon.
type Stop = [number, number, number, number] // [pctPos, r, g, b]
const HEAT_NEUTRAL = 'rgb(41,45,53)'
const HEAT_UP: Stop[] = [[0, 41, 45, 53], [0.5, 25, 84, 60], [1.25, 22, 120, 74], [2, 20, 152, 86], [3, 20, 194, 100]]
const HEAT_DN: Stop[] = [[0, 41, 45, 53], [0.5, 112, 47, 56], [1.25, 168, 46, 54], [2, 202, 44, 52], [3, 236, 42, 50]]
function rampColor(stops: Stop[], x: number): [number, number, number] {
  for (let i = 1; i < stops.length; i++) {
    if (x <= stops[i][0]) {
      const a = stops[i - 1], b = stops[i]
      const t = (x - a[0]) / (b[0] - a[0] || 1)
      return [1, 2, 3].map((k) => Math.round(a[k] + (b[k] - a[k]) * t)) as [number, number, number]
    }
  }
  const l = stops[stops.length - 1]; return [l[1], l[2], l[3]]
}
function heat(pct: number | null): string {
  if (pct == null) return HEAT_NEUTRAL
  const [r, g, b] = rampColor(pct >= 0 ? HEAT_UP : HEAT_DN, Math.min(3, Math.abs(pct)))
  return `rgb(${r},${g},${b})`
}
const pctStr = (p: number | null) => (p == null ? '' : `${p >= 0 ? '+' : ''}${p.toFixed(Math.abs(p) < 10 ? 1 : 0)}%`)
function money(n: number): string {
  const a = Math.abs(n), s = n < 0 ? '-' : '+'
  if (a >= 1e12) return `${s}$${(a / 1e12).toFixed(2)}T`
  if (a >= 1e9) return `${s}$${(a / 1e9).toFixed(a >= 1e10 ? 0 : 1)}B`
  if (a >= 1e6) return `${s}$${(a / 1e6).toFixed(0)}M`
  return `${s}$${Math.round(a)}`
}
const logoSrc = (sym: string) => `/api/logo?symbol=${encodeURIComponent(sym)}`

type Hover = { it: NameFlow; x: number; y: number; w: number; h: number }

export default function MoneyTreemap({ items, filter, onFilter }: { items: NameFlow[]; filter: Sector | 'All'; onFilter: (s: Sector | 'All') => void }) {
  const ref = useRef<HTMLDivElement>(null)
  const [w, setW] = useState(0)
  const [dense, setDense] = useState(true)
  const [sizeBy, setSizeBy] = useState<'cap' | 'move'>('cap')
  const [hover, setHover] = useState<Hover | null>(null)

  useEffect(() => {
    const el = ref.current; if (!el) return
    setW(el.clientWidth)
    const ro = new ResizeObserver((e) => setW(Math.round(e[0].contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const chips = useMemo(() => {
    const by = new Map<Sector, { net: number; count: number }>()
    for (const it of items) { const c = by.get(it.sector) ?? { net: 0, count: 0 }; c.net += it.capChange; c.count++; by.set(it.sector, c) }
    return SECTORS.filter((s) => by.has(s)).map((s) => ({ sector: s, ...by.get(s)! })).sort((a, b) => b.net - a.net)
  }, [items])

  const shown = useMemo(() => {
    const base = filter === 'All' ? items : items.filter((it) => it.sector === filter)
    const cap = filter === 'All' ? (dense ? 220 : 80) : base.length
    return base.slice(0, cap)
  }, [items, filter, dense])

  const h = w < 640 ? Math.round(Math.min(720, Math.max(520, w * 1.2))) : Math.round(Math.min(780, Math.max(520, w * 0.62)))
  const tiles = useMemo(
    () => squarify(shown.map((it) => ({ value: Math.max(sizeBy === 'cap' ? it.capNow : Math.abs(it.capChange), 0.0001), data: it })), 0, 0, w, h),
    [shown, w, h, sizeBy],
  )

  const onHover = useCallback((hv: Hover | null) => setHover(hv), [])

  return (
    <div>
      {/* controls: sector chips + view toggles */}
      <div className="mb-2.5 flex flex-wrap items-center gap-1.5">
        <Chip active={filter === 'All'} onClick={() => onFilter('All')} accent="text-zinc-200">All sectors</Chip>
        {chips.map((c) => (
          <Chip key={c.sector} active={filter === c.sector} onClick={() => onFilter(c.sector)} accent={SECTOR_ACCENT[c.sector]}>
            {c.sector} <span className={`ml-1 tabular-nums ${c.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{money(c.net)}</span>
          </Chip>
        ))}
        <div className="ml-auto flex items-center gap-1.5">
          <Segmented value={sizeBy} onChange={(v) => setSizeBy(v as 'cap' | 'move')} options={[{ v: 'cap', label: 'Market cap' }, { v: 'move', label: '$ moved' }]} />
          <Segmented value={dense ? 'dense' : 'clean'} onChange={(v) => setDense(v === 'dense')} options={[{ v: 'dense', label: 'Dense' }, { v: 'clean', label: 'Clean' }]} />
        </div>
      </div>

      <div ref={ref} className="relative w-full overflow-hidden rounded-xl bg-zinc-950 ring-1 ring-white/5" style={{ height: w ? h : 520 }}
        onMouseLeave={() => setHover(null)}>
        <TilesLayer tiles={tiles} onHover={onHover} />
        {hover && <HoverCard hv={hover} cw={w} ch={h} sizeBy={sizeBy} />}
        {!w && <div className="flex h-full items-center justify-center text-sm text-zinc-600">Loading map…</div>}
      </div>

      {/* legend */}
      <div className="mt-2 flex items-center justify-center gap-2 text-[10px] text-zinc-500">
        <span>Down</span>
        <span className="h-2.5 w-44 rounded-full" style={{ background: 'linear-gradient(90deg, rgb(236,42,50), rgb(202,44,52), rgb(168,46,54), rgb(41,45,53), rgb(22,120,74), rgb(20,152,86), rgb(20,194,100))' }} />
        <span>Up</span>
        <span className="ml-3 hidden sm:inline">Tile size = {sizeBy === 'cap' ? 'market cap' : 'money moved today'}</span>
      </div>
    </div>
  )
}

const TilesLayer = memo(function TilesLayer({ tiles, onHover }: { tiles: SqRect[]; onHover: (h: Hover | null) => void }) {
  return (
    <>
      {tiles.map((t) => {
        const it = t.data
        const minD = Math.min(t.w, t.h)
        // ticker size that fits the tile width (so it never clips)
        const fitFs = Math.min((t.w - 4) / Math.max(it.symbol.length * 0.62, 1), t.h * 0.5, 30)
        // tiers: big = logo+ticker+%(+$), med = logo+ticker, logoOnly = small tile → just the logo
        // (owner: "where not enough space, just do logos"), text = ticker fits, else bare color.
        const big = t.w >= 82 && t.h >= 92
        const med = !big && t.w >= 50 && t.h >= 52
        const logoOnly = !big && !med && t.w >= 28 && t.h >= 28
        const textOnly = !big && !med && !logoOnly && fitFs >= 7
        const showLogo = big || med || logoOnly
        const showTicker = big || med || textOnly
        const showPct = big || (med && t.h >= 68) || (textOnly && t.h >= 32 && t.w >= 34)
        const showMoney = big && t.h >= 118
        const logoSz = Math.round(big ? Math.min(minD * 0.32, 64) : med ? Math.min(minD * 0.42, 36) : Math.min(minD * 0.74, 46))
        const symFs = big ? Math.min((t.w - 6) / Math.max(it.symbol.length * 0.6, 1), minD * 0.2, 34)
          : med ? Math.min((t.w - 6) / Math.max(it.symbol.length * 0.6, 1), 15)
          : fitFs
        const subFs = Math.max(7, symFs * 0.6)
        return (
          <Link key={it.symbol} href={`/chart/${encodeURIComponent(it.symbol)}`}
            className="group absolute block" style={{ left: t.x, top: t.y, width: t.w, height: t.h, transition: 'left .3s ease, top .3s ease, width .3s ease, height .3s ease' }}
            onMouseEnter={() => onHover({ it, x: t.x, y: t.y, w: t.w, h: t.h })}>
            <div className="flex h-full w-full flex-col items-center justify-center gap-[3px] overflow-hidden rounded-[3px] px-1 text-center transition-all group-hover:z-10 group-hover:brightness-110 group-hover:ring-2 group-hover:ring-white/60"
              style={{ background: heat(it.dayPct), margin: 1, width: 'calc(100% - 2px)', height: 'calc(100% - 2px)', textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>
              {showLogo && (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={logoSrc(it.symbol)} alt="" loading="lazy"
                  className="rounded-md bg-white object-contain p-0.5 shadow-sm ring-1 ring-black/10" style={{ width: logoSz, height: logoSz }}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden' }} />
              )}
              {showTicker && <span className="font-bold leading-none text-white" style={{ fontSize: symFs }}>{it.symbol}</span>}
              {showPct && <span className="font-semibold leading-none text-white" style={{ fontSize: subFs }}>{pctStr(it.dayPct)}</span>}
              {showMoney && <span className="font-medium leading-none text-white/85" style={{ fontSize: Math.max(7, subFs * 0.9) }}>{money(it.capChange)}</span>}
            </div>
          </Link>
        )
      })}
    </>
  )
})

function HoverCard({ hv, cw, ch, sizeBy }: { hv: Hover; cw: number; ch: number; sizeBy: 'cap' | 'move' }) {
  const it = hv.it
  const CARD_W = 224, CARD_H = 138
  let left = Math.min(Math.max(hv.x + hv.w / 2 - CARD_W / 2, 4), Math.max(4, cw - CARD_W - 4))
  let top = hv.y - CARD_H - 8
  if (top < 4) top = Math.min(hv.y + hv.h + 8, ch - CARD_H - 4)
  const up = (it.dayPct ?? 0) >= 0
  return (
    <div className="pointer-events-none absolute z-20 rounded-xl border border-white/10 bg-zinc-900/95 p-3 shadow-2xl shadow-black/60 backdrop-blur" style={{ left, top, width: CARD_W }}>
      <div className="flex items-center gap-2.5">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={logoSrc(it.symbol)} alt="" className="h-9 w-9 shrink-0 rounded-md bg-white object-contain p-0.5 ring-1 ring-black/10" />
        <div className="min-w-0">
          <div className="truncate text-sm font-bold text-white">${it.symbol}</div>
          <div className={`truncate text-[11px] ${SECTOR_ACCENT[it.sector]}`}>{it.sector}</div>
        </div>
        <div className={`ml-auto text-sm font-bold tabular-nums ${up ? 'text-emerald-400' : 'text-rose-400'}`}>{pctStr(it.dayPct)}</div>
      </div>
      {it.name && <div className="mt-2 truncate text-[11px] text-zinc-400">{it.name}</div>}
      <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <Row k="Price" v={it.price != null ? `$${it.price.toFixed(2)}` : '—'} />
        <Row k="Mkt cap" v={money(it.capNow).replace('+', '')} />
        <Row k="Moved today" v={money(it.capChange)} vClass={it.capChange >= 0 ? 'text-emerald-400' : 'text-rose-400'} />
        <Row k="Sized by" v={sizeBy === 'cap' ? 'Market cap' : '$ moved'} />
      </div>
    </div>
  )
}
function Row({ k, v, vClass = 'text-zinc-200' }: { k: string; v: string; vClass?: string }) {
  return <><span className="text-zinc-500">{k}</span><span className={`text-right font-semibold tabular-nums ${vClass}`}>{v}</span></>
}

function Chip({ active, onClick, accent, children }: { active: boolean; onClick: () => void; accent: string; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${active ? 'border-white/30 bg-white/10 text-white' : `border-zinc-800 bg-zinc-900/50 hover:border-zinc-700 ${accent}`}`}>
      {children}
    </button>
  )
}
function Segmented({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: { v: string; label: string }[] }) {
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900/50 p-0.5">
      {options.map((o) => (
        <button key={o.v} onClick={() => onChange(o.v)}
          className={`rounded-md px-2 py-1 text-[11px] font-semibold transition-colors ${value === o.v ? 'bg-white/10 text-white' : 'text-zinc-400 hover:text-zinc-200'}`}>{o.label}</button>
      ))}
    </div>
  )
}
